import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';
import TierDonut from './components/TierDonut';
import SubscriptionTrendChart from './components/SubscriptionTrendChart';

const TIER_BADGE_COLORS = {
  taster: 'bg-blue-50 text-blue-700',
  collector: 'bg-purple-50 text-purple-700',
  enthusiast: 'bg-pink-50 text-pink-700',
  connoisseur: 'bg-amber-50 text-amber-700',
  vip: 'bg-red-50 text-red-700',
  claimed: 'bg-gray-100 text-gray-700',
  enhanced: 'bg-blue-50 text-blue-700',
  premium: 'bg-purple-50 text-purple-700',
  reserve: 'bg-red-50 text-red-700',
};

function TierBadge({ tier, name }) {
  const cls = TIER_BADGE_COLORS[tier] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {name || tier}
    </span>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">{value}</dd>
        {sub && <div className="mt-2 text-sm text-gray-600">{sub}</div>}
      </div>
    </div>
  );
}

function ExpiringTable({ title, rows, kind }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <p className="text-gray-500 text-sm">Nothing expiring in the next 7 days.</p>
      </div>
    );
  }
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
              {kind === 'individual' ? 'User' : 'Listing'}
            </th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Tier</th>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Expires</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="py-2">
                <Link
                  to={kind === 'individual' ? `/admin/users/${r.id}` : `/admin/listings/${r.id}`}
                  className="text-red-600 hover:text-red-900"
                >
                  {r.name}
                </Link>
                {r.email && <div className="text-gray-400 text-xs">{r.email}</div>}
              </td>
              <td className="py-2"><TierBadge tier={r.tier} /></td>
              <td className="py-2 text-sm text-gray-500">
                {r.expiresAt
                  ? formatDistance(new Date(r.expiresAt), new Date(), { addSuffix: true })
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentActivityTable({ rows, kind, expanded, onToggle }) {
  const title = kind === 'individual' ? 'Recently updated subscribers' : 'Recently updated listings';
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <span className="text-sm text-gray-500">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      {expanded && (
        <>
          <p className="text-xs text-gray-500 mt-2 mb-3 italic">
            Records updated in the last 30 days. Includes non-subscription edits — proxy until a Stripe-event audit table exists.
          </p>
          {!rows || rows.length === 0 ? (
            <p className="text-gray-500 text-sm">Nothing in the last 30 days.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                    {kind === 'individual' ? 'User' : 'Listing'}
                  </th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Tier</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2">
                      <Link
                        to={kind === 'individual' ? `/admin/users/${r.id}` : `/admin/listings/${r.id}`}
                        className="text-red-600 hover:text-red-900"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td className="py-2"><TierBadge tier={r.tier} /></td>
                    <td className="py-2 text-sm text-gray-700">{r.status || '—'}</td>
                    <td className="py-2 text-sm text-gray-500">
                      {r.changedAt
                        ? formatDistance(new Date(r.changedAt), new Date(), { addSuffix: true })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

function OverridesSection({ overrides }) {
  if (!overrides) return null;
  const { activeCount, expiringCount, byGap, bySource, expiringSoon } = overrides;
  if (activeCount === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Overrides &amp; comps</h3>
        <p className="text-gray-500 text-sm">
          No users currently have an active access override. Comps appear here when
          you grant a higher tier than the user is paying for.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Overrides &amp; comps</h3>
          <p className="text-xs text-gray-500 mt-1">
            Users paying for one tier with access granted to a higher tier (beta testers, comps, passes).
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold text-gray-900">{activeCount}</div>
          <div className="text-xs text-gray-500">active comps</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Pay vs effective</h4>
          {byGap.length === 0 ? (
            <p className="text-gray-500 text-sm">No active gaps.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Paying</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Effective</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Users</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {byGap.map((g) => (
                  <tr key={`${g.paidTier}-${g.effectiveTier}`}>
                    <td className="py-2"><TierBadge tier={g.paidTier} name={g.paidName} /></td>
                    <td className="py-2">
                      <span className="text-gray-400 mr-1">→</span>
                      <TierBadge tier={g.effectiveTier} name={g.effectiveName} />
                    </td>
                    <td className="py-2 text-right font-medium">{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">By source</h4>
          {bySource.length === 0 ? (
            <p className="text-gray-500 text-sm">—</p>
          ) : (
            <ul className="space-y-1">
              {bySource.map((s) => (
                <li key={s.source} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 capitalize">{s.source}</span>
                  <span className="text-gray-900 font-medium">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {expiringCount > 0 && (
        <div className="mt-6 border-t border-gray-100 pt-4">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            Expiring in next 7 days
          </h4>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">User</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Currently</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Drops to</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Source</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {expiringSoon.map((o) => (
                <tr key={o.id}>
                  <td className="py-2">
                    <Link to={`/admin/users/${o.id}`} className="text-red-600 hover:text-red-900">
                      {o.name || o.email}
                    </Link>
                  </td>
                  <td className="py-2"><TierBadge tier={o.effectiveTier} name={o.effectiveName} /></td>
                  <td className="py-2"><TierBadge tier={o.paidTier} name={o.paidName} /></td>
                  <td className="py-2 text-sm text-gray-600 capitalize">{o.source}</td>
                  <td className="py-2 text-sm text-gray-500">
                    {formatDistance(new Date(o.expiresAt), new Date(), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistoryTable({ history }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Snapshot history</h3>
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Date</th>
            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Ind. Active</th>
            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Ind. Free</th>
            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Ind. Trialing</th>
            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Listing Active</th>
            <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Listing Free</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {history.map((row) => (
            <tr key={row.date}>
              <td className="py-2 text-sm text-gray-700">{format(new Date(row.date), 'MMM d, yyyy')}</td>
              <td className="py-2 text-right">{row.individual.totalActive}</td>
              <td className="py-2 text-right text-gray-500">{row.individual.totalFree}</td>
              <td className="py-2 text-right text-gray-500">{row.individual.totalTrialing}</td>
              <td className="py-2 text-right">{row.listing.totalActive}</td>
              <td className="py-2 text-right text-gray-500">{row.listing.totalFree}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-400 mt-3">
        Snapshots are auto-built every Sunday at 2:00 UTC.
      </p>
    </div>
  );
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const [report, setReport] = useState();
  const [history, setHistory] = useState();
  const [error, setError] = useState();
  const [building, setBuilding] = useState(false);
  const [buildMsg, setBuildMsg] = useState();
  const [recentExpanded, setRecentExpanded] = useState({ individual: false, listing: false });

  const loadReport = async () => {
    try {
      const result = await client.service('/admin/subscription-report').find();
      setReport(result);
    } catch (err) {
      setError(err.message || 'Failed to load subscription report');
    }
  };

  const loadHistory = async () => {
    try {
      const result = await client.service('/admin/subscription-snapshot').find();
      setHistory(result.byDate || []);
    } catch (err) {
      setError(err.message || 'Failed to load snapshot history');
    }
  };

  useEffect(() => {
    loadReport();
    loadHistory();
  }, []);

  const handleBuildSnapshot = async () => {
    setBuilding(true);
    setBuildMsg(null);
    try {
      const result = await client.service('/admin/subscription-snapshot').create({});
      setBuildMsg({ ok: true, text: `Snapshot built for ${result.date} (${result.recordsCreated} rows)` });
      await loadHistory();
    } catch (err) {
      setBuildMsg({ ok: false, text: err.message || 'Failed to build snapshot' });
    } finally {
      setBuilding(false);
    }
  };

  const handleTierClick = (tier, kind) => {
    // Drill-down: navigate to the existing users/listings page filtered by tier.
    if (kind === 'individual') {
      navigate(`/admin/users?accessLevel=${tier}`);
    } else {
      navigate(`/admin/listings?subscriptionTier=${tier}`);
    }
  };

  const generatedAt = useMemo(
    () => (report?.generatedAt ? format(new Date(report.generatedAt), 'MMM d, yyyy h:mm a') : ''),
    [report]
  );

  // Heuristic for "needs attention": past-due is the only urgent signal in the report.
  // We don't have a dedicated count, so derive from breakdown.byTier minus active count.
  const needsAttentionCount = useMemo(() => {
    if (!history || history.length === 0) return null;
    const latest = history[0];
    return (latest?.individual?.totalPastDue || 0) + (latest?.listing?.totalPastDue || 0);
  }, [history]);

  return (
    <Page title="Subscriptions">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: `/admin/reports` },
            { name: 'Subscriptions' },
          ]} />
        </nav>
      </div>

      <div className="py-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {!report && !error && <p>Loading…</p>}

        {report && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
                <p className="text-xs text-gray-500 mt-1">Generated {generatedAt}</p>
              </div>
              <button
                type="button"
                onClick={handleBuildSnapshot}
                disabled={building}
                className="px-4 py-2 bg-primary text-white rounded hover:opacity-90 text-sm font-medium disabled:opacity-50"
              >
                {building ? 'Building…' : 'Snapshot today'}
              </button>
            </div>

            {buildMsg && (
              <div className={`px-4 py-3 rounded mb-6 ${buildMsg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {buildMsg.text}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
              <KpiCard
                label="Active paid individuals"
                value={report.summary.individual.activeCount}
                sub={`${report.summary.individual.conversionRate}% of ${report.summary.individual.totalUsers} users`}
              />
              <KpiCard
                label="Active paid listings"
                value={report.summary.listing.activeCount}
                sub={`${report.summary.listing.conversionRate}% of ${report.summary.listing.totalListings} listings`}
              />
              <KpiCard
                label="Free individuals"
                value={report.summary.individual.freeCount}
              />
              <KpiCard
                label="Comped users"
                value={report.overrides?.activeCount ?? 0}
                sub={report.overrides?.expiringCount > 0
                  ? `${report.overrides.expiringCount} expiring within 7 days`
                  : 'paid one tier, granted higher'}
              />
              <KpiCard
                label="Needs attention"
                value={needsAttentionCount !== null ? needsAttentionCount : '—'}
                sub={needsAttentionCount !== null
                  ? 'past-due / unpaid (from latest snapshot)'
                  : 'build a snapshot to compute'}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Individuals by tier</h3>
                <p className="text-xs text-gray-500 mb-4">Click a slice to see users in that tier.</p>
                <TierDonut
                  kind="individual"
                  tiers={report.breakdown.individual.byTier}
                  freeCount={report.summary.individual.freeCount}
                  onTierClick={handleTierClick}
                />
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Listings by tier</h3>
                <p className="text-xs text-gray-500 mb-4">Click a slice to see listings in that tier.</p>
                <TierDonut
                  kind="listing"
                  tiers={report.breakdown.listing.byTier}
                  freeCount={report.summary.listing.freeCount}
                  onTierClick={handleTierClick}
                />
              </div>
            </div>

            <div className="mb-8">
              <OverridesSection overrides={report.overrides} />
            </div>

            <div className="mb-8">
              <SubscriptionTrendChart history={history} />
            </div>

            <h2 className="text-lg font-semibold text-gray-800 mb-3">Action items</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
              <ExpiringTable
                title="Individuals expiring soon"
                rows={report.expiringSoon.individual}
                kind="individual"
              />
              <ExpiringTable
                title="Listings expiring soon"
                rows={report.expiringSoon.listing}
                kind="listing"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
              <RecentActivityTable
                rows={report.recentActivity.individual}
                kind="individual"
                expanded={recentExpanded.individual}
                onToggle={() => setRecentExpanded((s) => ({ ...s, individual: !s.individual }))}
              />
              <RecentActivityTable
                rows={report.recentActivity.listing}
                kind="listing"
                expanded={recentExpanded.listing}
                onToggle={() => setRecentExpanded((s) => ({ ...s, listing: !s.listing }))}
              />
            </div>

            <HistoryTable history={history} />
          </>
        )}
      </div>
    </Page>
  );
}
